import { ShuffleIcon } from '@phosphor-icons/react'
import clsx from 'clsx'
import Link from 'next/link'
import { useState } from 'react'
import { FahrlehrerCh } from '@/src/@types/blinkadmin'
import { PageInfo } from '@/src/@types/models'
import { FlchTeacherShuffleBlock } from '@/src/@types/statamic'
import { flchUrl, get } from '@/src/BlinkAdminApiClient'
import Button from '@/src/components/core/Button'
import Picture from '@/src/components/elements/Picture'
import Container from '@/src/components/layout/Container'

const COLUMN_SIZES = [2, 3, 2]

export async function getStaticProps(props: FlchTeacherShuffleBlock) {
  const teamMembers = await get<FahrlehrerCh.TeamMemberPreview[]>(
    flchUrl('team-members')
  )

  return { ...props, teamMembers: teamMembers ?? [] }
}

export default function Component({
  title,
  text,
  link_link,
  link_text,
  teamMembers,
  pageInfo,
}: FlchTeacherShuffleBlock & {
  teamMembers: FahrlehrerCh.TeamMemberPreview[]
  pageInfo: PageInfo
}) {
  const [members, setMembers] = useState(teamMembers)
  const [isShuffling, setIsShuffling] = useState(false)

  const onShuffle = async () => {
    setIsShuffling(true)
    try {
      const next = await get<FahrlehrerCh.TeamMemberPreview[]>(
        flchUrl('team-members')
      )
      if (next?.length) {
        setMembers(next)
      }
    } finally {
      setIsShuffling(false)
    }
  }

  let offset = 0
  const columns = COLUMN_SIZES.map((size) => {
    const column = members.slice(offset, offset + size)
    offset += size
    return column
  })

  return (
    <section className={clsx({ 'mt-20 md:mt-32': pageInfo.newSpacing })}>
      <Container>
        <div className="grid gap-10 md:grid-cols-2 md:items-center md:gap-14">
          <div className="order-2 md:order-1">
            <div
              className={clsx(
                'grid grid-cols-3 items-center gap-3.5 transition-opacity duration-300',
                isShuffling && 'opacity-70'
              )}
            >
              {columns.map((column, index) => (
                <div key={index} className="flex flex-col gap-3.5">
                  {column.map((member, memberIndex) => (
                    <TeacherAvatar
                      key={`${member.url}-${memberIndex}`}
                      member={member}
                    />
                  ))}
                </div>
              ))}
            </div>
            <div className="mt-6 flex justify-center">
              <Button
                version="white-gray"
                size="small"
                icon={<ShuffleIcon weight="bold" />}
                onClick={onShuffle}
                loading={isShuffling}
                className="shadow-sm"
              >
                Shuffle
              </Button>
            </div>
          </div>
          <div className="order-1 flex flex-col gap-6 md:order-2">
            <div className="flex flex-col gap-4 md:gap-6">
              <h2 className="text-4xl font-black text-balance md:text-20xl">
                {title}
              </h2>
              <p className="text-gray-800 md:text-lg">{text}</p>
            </div>
            {link_link?.url && link_text && (
              <Button
                as={Link}
                href={link_link.url}
                version="blue"
                className="self-start"
              >
                {link_text}
              </Button>
            )}
          </div>
        </div>
      </Container>
    </section>
  )
}

function TeacherAvatar({ member }: { member: FahrlehrerCh.TeamMemberPreview }) {
  return (
    <Link
      href={member.url}
      className="relative block aspect-7/5 overflow-hidden rounded-lg bg-gray-100"
    >
      {member.avatar && (
        <Picture
          permalink={member.avatar.url}
          fill
          alt={`Bild von ${member.name}`}
          className="object-cover"
          sizes="(min-width: 768px) 200px, 33vw"
        />
      )}
    </Link>
  )
}
